import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { AppButton } from "site/ui";
import { IGetQuizQuestions, useQuizStore } from "store";
import { ICourse, IModule, IQuizYear } from "utils/types";
import { routeNames } from "utils";
import { CardBadge } from "common";

const Container = styled.div`
  max-width: 1000px;
  padding: 2rem 1.5rem;
  font-family: "Manrope", sans-serif;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: 14px;
  color: #6b7280;

  a {
    color: #5575f7;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Header = styled.div`
  display: flex;
  gap: 2rem;
  flex-direction: column;
`;

const TagGroup = styled.div`
  display: flex;
  gap: 1rem;
  overflow: scroll;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const CourseImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0 0 1rem 0;
`

const Title = styled.div`
  font-size: 40px;
  font-weight: 600;
  color: #111827;
  font-family: "Manrope-Bold";
  line-height: 48px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const Tag = styled.a<{ variant?: "primary" | "secondary" }>`
  padding: 16px;
  border-radius: 32px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  justify-content: center;
  display: flex;
  min-width: fit-content;

  ${(props) =>
    props.variant === "secondary"
      ? `
    background: #F3F4F6;
    color: #5b5bda;
  `
      : `
    background: #5575f7;
    color: white;
  `}
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 2rem 0 1rem 0;
`;

const SubSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 1.5rem 0 1rem 0;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;

  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
    font-size: 14px;
    color: #4b5563;
    line-height: 1.6;

    &::before {
      content: "•";
      position: absolute;
      left: 0;
      color: #5575f7;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
`;

const YearsList = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const YearTag = styled.span`
  background: #f3f4f6;
  color: #5575f7;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const AvailableQuestions = styled.div`
  border-radius: 12px;
`;

const QuestionHeader = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 1rem;
`;

export interface CourseFullViewProps {
  internal?: boolean;
  course?: ICourse;
  modules: IModule[];
  onMoveToCourseStudy: () => void;
  onGetQuizQuestions: IGetQuizQuestions
  onGetQuizYears: (courseId: string) => Promise<IQuizYear[]>;
}
export const CourseFullView: React.FC<CourseFullViewProps> = ({
  course,  onGetQuizQuestions, onGetQuizYears,
  onMoveToCourseStudy, modules, internal
}) => {
  const [quizYears, setQuizYears] = useState<IQuizYear[]>([]);
  const { openQuiz } = useQuizStore();


  useEffect(() => {
    if(course){
      onGetQuizYears(course.id).then(setQuizYears)
    }
  }, [JSON.stringify(course)])
  const handleTakeQuizClick = useCallback(() => {
    openQuiz({
      quizYears,
      onGetQuizQuestions
    });
  }, [onGetQuizQuestions, JSON.stringify(quizYears)]);


  return (
    <Container>
      <Breadcrumb>
        <Link to={routeNames.dashboard}>Home</Link> &gt;
        <span>Overview</span>
      </Breadcrumb>

      <Header>
        <CourseImage src={course?.image} alt={course?.category} />
        <HeaderContent>
          <TitleContainer>
            <Title>{course?.title}</Title>
            <CardBadge style={{ fontSize: 16 }} $isPremium={course?.courseType === "premium"}>{course?.courseType}</CardBadge>
          </TitleContainer>
          <Description>
            {course?.description}
          </Description>
        </HeaderContent>
      </Header>

      <AvailableQuestions>
        <QuestionHeader>Available past questions:</QuestionHeader>
        <YearsList>
          {course?.availableQuizzes?.map((item, index) => {
            return (
              <YearTag key={item.quizId+"---"+index}>{item.quizYear}</YearTag>
            )
          })}
        </YearsList>
      </AvailableQuestions>

      <ButtonGroup>
        <AppButton variant="primary" onClick={onMoveToCourseStudy}>
          Start this course
        </AppButton>
        {quizYears?.length > 0 && <AppButton variant="accent" onClick={handleTakeQuizClick}>
          Take quiz
        </AppButton>}
      </ButtonGroup>

      <TagGroup>
        <Tag {...(internal ? {} : { href: "#outline"})} variant="secondary">Course outline</Tag>
        <Tag {...(internal ? {} : { href: "#why"})} variant="secondary">Why you need this course</Tag>
        <Tag {...(internal ? {} : { href: "#requirements"})} variant="secondary">Requirements for completing the course</Tag>
        {course?.availableQuizzes && course?.availableQuizzes.length > 0 && <Tag href="#quiz" variant="secondary">Course quiz </Tag>}
        <Tag {...(internal ? {} : { href: "#tips"})} variant="secondary">Study and preparation tips </Tag>
      </TagGroup>

      <SectionTitle id="outline">Course outline</SectionTitle>
      <List>
      {modules?.map((item, index) => {
        return (
          <li key={`${item.id}--${index}`}>
            <SubSectionTitle>{item.title}</SubSectionTitle>
            <div dangerouslySetInnerHTML={{__html: item.description || ""}}/>
            {item.lessons && item.lessons?.length > 0 && (
              <List>
                {item.lessons?.map((lesson, index) => {
                  return (
                    <li key={`${lesson.id}--${index}`}>
                      <SubSectionTitle style={{fontSize: 14 }}>{lesson.title}</SubSectionTitle>
                      {/* <div dangerouslySetInnerHTML={{__html: lesson.description || ""}}/> */}
                    </li>
                  )
                })}
              </List>
            )}
          </li>
        )
      })}
      </List>

      <SectionTitle id="why">Why you need this course</SectionTitle>
      <List>
        <div dangerouslySetInnerHTML={{__html: course?.learningObjectives || ""}}/>
      </List>

      <SectionTitle id="requirements">Requirements</SectionTitle>
      <List>
        <div dangerouslySetInnerHTML={{__html: course?.prerequisites || ""}}/>
      </List>

      <SectionTitle id="quiz">Course quiz</SectionTitle>
      <List>
        <div dangerouslySetInnerHTML={{__html: course?.quizDescription || ""}}/>
      </List>

      <SectionTitle id="tips">Study and preparation tips</SectionTitle>
      <List>
        <div dangerouslySetInnerHTML={{__html: course?.quizPrepTips || ""}}/>
      </List>
    </Container>
  );
};
